import { InfoCircleOutlined, MinusOutlined } from "@ant-design/icons";
import { Tooltip } from "antd";
import type { FC, ReactNode } from "react";

import type { ChallengeRewardOption } from "../../../../queries/game-pass/gamePassOptions";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import { Input, Select } from "../../../shared";
import {
  humanizeParamName,
  isMinLegsBelowFloor,
  sanitizeIntegerInput,
  sanitizeParamInput,
  MIN_LEGS_FLOOR_VALUE,
  MIN_LEGS_PARAM,
  type LabeledOption,
  type MonthlyTierDraft,
} from "../monthlyChallengeData";

import styles from "./MonthlyTierRow.module.css";

export type MonthlyTierRowProps = {
  index: number;
  tier: MonthlyTierDraft;
  rewardTypeOptions: ReadonlyArray<LabeledOption<string>>;
  /** The selected catalogue option — drives the amount placeholder and the
   *  bonus param inputs (same as Season `ChallengeCard`). */
  rewardOption?: ChallengeRewardOption;
  onChange: (next: MonthlyTierDraft) => void;
  onRemove: () => void;
  /** The last remaining tier can't be removed — hides the remove control. */
  removable: boolean;
  /** Read-only mode (locked / Active view): disables the inputs + select and
   *  hides the remove control so the tier renders as inspect-only. */
  disabled?: boolean;
  idPrefix: string;
};

/**
 * A single tier of a monthly challenge: the threshold to reach, the currency
 * reward (amount + unit) earned for reaching it, and an optional XP bonus.
 * "Follow for follow" tiers are escalating follower counts (e.g. 10 → 20 → 30),
 * each with its own reward.
 */
const MonthlyTierRow: FC<MonthlyTierRowProps> = ({
  index,
  tier,
  rewardTypeOptions,
  rewardOption,
  onChange,
  onRemove,
  removable,
  disabled = false,
  idPrefix,
}) => {
  const tierNumber = index + 1;
  const paramDefs =
    rewardOption?.kind === "bonus" ? rewardOption.params ?? [] : [];

  return (
    <div className={styles.row} data-testid={`${idPrefix}-tier-${index}`}>
      <div className={styles.inner}>
        <label className={styles.field}>
          <span className={styles.label}>
            {`${AppStrings.MONTHLY_CHALLENGE_TIER_PREFIX} ${tierNumber}`}
          </span>
          <Input
            value={tier.threshold}
            inputMode="numeric"
            disabled={disabled}
            placeholder={AppStrings.MONTHLY_CHALLENGE_TIER_VALUE_PLACEHOLDER}
            onChange={(e) =>
              onChange({
                ...tier,
                threshold: sanitizeIntegerInput(e.target.value),
              })
            }
            data-testid={`${idPrefix}-tier-${index}-threshold`}
          />
        </label>

        <div className={`${styles.field} ${styles.rewardField}`}>
          <span className={styles.label}>
            {AppStrings.MONTHLY_CHALLENGE_TIER_REWARD_LABEL}
          </span>
          <div className={styles.rewardControl}>
            <Input
              value={tier.rewardAmount}
              inputMode="numeric"
              disabled={disabled}
              placeholder={
                rewardOption?.amountLabel ??
                AppStrings.MONTHLY_CHALLENGE_TIER_REWARD_PLACEHOLDER
              }
              onChange={(e) =>
                onChange({
                  ...tier,
                  rewardAmount: sanitizeIntegerInput(e.target.value),
                })
              }
              data-testid={`${idPrefix}-tier-${index}-reward-amount`}
            />
            <Select<string>
              className={styles.rewardTypeSelect}
              classNames={{ popup: { root: styles.rewardTypeDropdown } }}
              value={tier.rewardType}
              disabled={disabled}
              options={[...rewardTypeOptions]}
              popupMatchSelectWidth={false}
              aria-label={AppStrings.MONTHLY_CHALLENGE_TIER_REWARD_TYPE_ARIA}
              onChange={(value) => onChange({ ...tier, rewardType: value })}
              data-testid={`${idPrefix}-tier-${index}-reward-type`}
            />
          </div>
        </div>

        <label className={`${styles.field} ${styles.xpField}`}>
          <div className={styles.rewardTop}>
            <span className={styles.label}>
              {AppStrings.GAME_PASS_MONTHLY_CHALLENGE_TIER_XP_LABEL}
            </span>
            {removable && !disabled ? (
              <button
                type="button"
                className={styles.removeButton}
                aria-label={AppStrings.MONTHLY_CHALLENGE_TIER_REMOVE_ARIA}
                onClick={onRemove}
                data-testid={`${idPrefix}-tier-${index}-remove`}
              >
                <MinusOutlined className={styles.removeIcon} />
              </button>
            ) : null}
          </div>
          <Input
            value={tier.xpReward}
            inputMode="numeric"
            disabled={disabled}
            placeholder={AppStrings.GAME_PASS_MONTHLY_CHALLENGE_TIER_XP_PLACEHOLDER}
            onChange={(e) =>
              onChange({
                ...tier,
                xpReward: sanitizeIntegerInput(e.target.value),
              })
            }
            data-testid={`${idPrefix}-tier-${index}-xp-reward`}
          />
        </label>
      </div>

      {paramDefs.length > 0 && (
        <div
          className={styles.rewardParams}
          data-testid={`${idPrefix}-tier-${index}-reward-params`}
        >
          {paramDefs.map((param) => {
            const isMinLegs = param.name === MIN_LEGS_PARAM;
            const belowFloor =
              !disabled &&
              isMinLegs &&
              isMinLegsBelowFloor(tier.params?.[MIN_LEGS_PARAM]);
            const errorId = `${idPrefix}-tier-${index}-param-${param.name}-error`;

            let requirementMarker: ReactNode = ` (${AppStrings.CHALLENGE_REWARD_PARAM_OPTIONAL})`;
            if (isMinLegs) {
              requirementMarker = ` (${formatString(
                AppStrings.GAME_PASS_MONTHLY_CHALLENGE_TIER_PARAM_MIN_LEGS,
                { min: MIN_LEGS_FLOOR_VALUE },
              )})`;
            } else if (param.required) {
              requirementMarker = (
                <span className={styles.rewardParamRequired} aria-hidden="true">
                  {" *"}
                </span>
              );
            }

            return (
              <label key={param.name} className={styles.rewardParamField}>
                <span className={styles.rewardParamLabel}>
                  <span className={styles.rewardParamLabelText}>
                    {humanizeParamName(param.name)}
                    {requirementMarker}
                  </span>
                  {param.description && (
                    <Tooltip title={param.description}>
                      <InfoCircleOutlined
                        className={styles.rewardParamInfoIcon}
                        role="img"
                        aria-label={param.description}
                        data-testid={`${idPrefix}-tier-${index}-param-info-${param.name}`}
                      />
                    </Tooltip>
                  )}
                </span>
                <Input
                  value={tier.params?.[param.name] ?? ""}
                  disabled={disabled}
                  tone={belowFloor ? "error" : "default"}
                  aria-invalid={belowFloor || undefined}
                  aria-describedby={belowFloor ? errorId : undefined}
                  inputMode={
                    param.type === "integer" || param.type === "number"
                      ? "decimal"
                      : "text"
                  }
                  placeholder={
                    param.default !== null && param.default !== undefined
                      ? String(param.default)
                      : undefined
                  }
                  onChange={(e) => {
                    onChange({
                      ...tier,
                      params: {
                        ...(tier.params ?? {}),
                        [param.name]: sanitizeParamInput(
                          param.type,
                          e.target.value,
                        ),
                      },
                    });
                  }}
                  data-testid={`${idPrefix}-tier-${index}-param-${param.name}`}
                />
                {belowFloor ? (
                  <span
                    id={errorId}
                    className={styles.paramError}
                    role="alert"
                    data-testid={errorId}
                  >
                    {formatString(
                      AppStrings.GAME_PASS_MONTHLY_CHALLENGE_TIER_MIN_LEGS_ERROR,
                      { min: MIN_LEGS_FLOOR_VALUE },
                    )}
                  </span>
                ) : null}
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default MonthlyTierRow;
